import type { Geometry } from 'geojson';
import { NotificationType } from 'hooks/useNotification';
import type {
  OrgEventChecklistItemDef,
  OrgEventNotifyConfig,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import {
  escalationPlanErrors,
  type EscalationPlan,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/escalationPlanModel';
import { withoutLockedChannels } from 'components/MapPage/CorporateViewTabs/EventsTab/automationNotifyForm';
import type {
  Scenario,
  ScenarioAudienceConfig,
  ScenarioWritePayload,
} from 'hooks/scenarios/scenarioHooks';

/**
 * The scenario editor's form state, and the mapping to and from the stored
 * scenario.
 *
 * Flat rather than nested, because the form is a set of react-hook-form fields
 * and the nesting only exists in storage. Pure so the mapping and the
 * validation can be tested without rendering anything.
 */
export type ScenarioFormValues = {
  name: string;
  description: string;
  /** Audience */
  userIds: string[];
  teamIds: string[];
  aorItemIds: string[];
  geometry: Geometry | null;
  /** Message */
  notificationTypes: string[];
  priority: string;
  confirmReceipt: boolean;
  titleTemplate: string;
  bodyTemplate: string;
  /** Follow-up and work */
  escalationPlan: EscalationPlan | null;
  checklistItems: OrgEventChecklistItemDef[];
};

export function blankScenarioForm(): ScenarioFormValues {
  return {
    name: '',
    description: '',
    userIds: [],
    teamIds: [],
    aorItemIds: [],
    geometry: null,
    notificationTypes: [NotificationType.IN_APP],
    priority: 'watch',
    confirmReceipt: false,
    titleTemplate: '',
    bodyTemplate: '',
    escalationPlan: null,
    checklistItems: [],
  };
}

export function scenarioToForm(scenario: Scenario): ScenarioFormValues {
  const notify = scenario.notifyConfig ?? {};
  const notificationTypes = withoutLockedChannels(notify.notificationTypes);

  return {
    name: scenario.name,
    description: scenario.description ?? '',
    userIds: scenario.audienceConfig?.userIds ?? [],
    teamIds: scenario.audienceConfig?.teamIds ?? [],
    aorItemIds: scenario.audienceConfig?.aorItemIds ?? [],
    geometry: scenario.audienceConfig?.geometry ?? null,
    notificationTypes: notificationTypes.length
      ? notificationTypes
      : [NotificationType.IN_APP],
    priority: notify.priority || 'watch',
    confirmReceipt: Boolean(notify.confirmReceipt),
    titleTemplate: notify.titleTemplate ?? '',
    bodyTemplate: notify.bodyTemplate ?? '',
    escalationPlan: scenario.escalationPlan ?? null,
    checklistItems: scenario.checklistConfig?.items ?? [],
  };
}

export function audienceFromForm(
  values: ScenarioFormValues
): ScenarioAudienceConfig {
  return {
    userIds: values.userIds ?? [],
    teamIds: values.teamIds ?? [],
    aorItemIds: values.aorItemIds ?? [],
    geometry: values.geometry ?? null,
  };
}

export function notifyConfigFromForm(
  values: ScenarioFormValues,
  existing?: OrgEventNotifyConfig | null
): OrgEventNotifyConfig {
  const notificationTypes = withoutLockedChannels(values.notificationTypes);
  return {
    ...existing,
    notificationTypes: notificationTypes.length
      ? notificationTypes
      : [NotificationType.IN_APP],
    priority: values.priority || 'watch',
    confirmReceipt: Boolean(values.confirmReceipt),
    // Falls back to the scenario's own name and description, so a scenario with
    // nothing typed into the template fields still sends something meaningful.
    titleTemplate: values.titleTemplate.trim() || values.name,
    bodyTemplate:
      values.bodyTemplate.trim() || values.description || values.name,
    // Recipients live on the scenario's audience config, not here. Leaving them
    // in both places would give a launch two answers about who to notify.
    userIds: [],
    teamIds: [],
  };
}

export function formToScenarioPayload(
  values: ScenarioFormValues,
  existing?: Scenario | null
): ScenarioWritePayload {
  return {
    name: values.name.trim(),
    description: values.description.trim() || null,
    audienceConfig: audienceFromForm(values),
    notifyConfig: notifyConfigFromForm(values, existing?.notifyConfig),
    // A cascade advances on silence, and without a confirmation affordance every
    // step would fire for everyone. The server rejects that combination, so the
    // ladder is dropped here rather than posted and bounced.
    escalationPlan: values.confirmReceipt ? values.escalationPlan : null,
    checklistConfig: { items: values.checklistItems },
  };
}

export function audienceIsEmpty(values: ScenarioFormValues): boolean {
  return (
    values.userIds.length === 0 &&
    values.teamIds.length === 0 &&
    values.aorItemIds.length === 0 &&
    !values.geometry
  );
}

/** Why this scenario cannot be saved yet, in the order to fix it. */
export function scenarioFormErrors(values: ScenarioFormValues): string[] {
  const errors: string[] = [];

  if (!values.name.trim()) {
    errors.push('Give the scenario a name.');
  }

  // A scenario with neither an audience nor a checklist does nothing at all,
  // which is worth refusing rather than saving as an empty button.
  if (audienceIsEmpty(values) && values.checklistItems.length === 0) {
    errors.push(
      'Add an audience to notify, or checklist items to create. A scenario with neither does nothing.'
    );
  }

  if (values.notificationTypes.length === 0 && !audienceIsEmpty(values)) {
    errors.push('Choose at least one channel to notify on.');
  }

  if (values.confirmReceipt && values.escalationPlan) {
    errors.push(...escalationPlanErrors(values.escalationPlan));
  }

  return errors;
}

/** One line for the scenarios table, saying what launching this would do. */
export function describeScenario(scenario: Scenario): string {
  const parts: string[] = [];

  const audience = scenario.audienceConfig ?? {};
  const named =
    (audience.userIds?.length ?? 0) + (audience.teamIds?.length ?? 0);
  const areas = audience.aorItemIds?.length ?? 0;

  if (named > 0) {
    parts.push(`${named} named recipient${named === 1 ? '' : 's'}`);
  }
  if (areas > 0) {
    parts.push(`${areas} area${areas === 1 ? '' : 's'}`);
  }
  if (audience.geometry) {
    parts.push('a drawn area');
  }
  if (parts.length === 0) {
    parts.push('no audience');
  }

  const actions = (scenario.checklistConfig?.items ?? []).filter(
    (item) => item.kind === 'org_action'
  ).length;
  if (actions > 0) {
    parts.push(`${actions} task${actions === 1 ? '' : 's'}`);
  }

  const steps = scenario.escalationPlan?.steps.length ?? 0;
  if (steps > 0) {
    parts.push(`${steps} follow-up step${steps === 1 ? '' : 's'}`);
  }

  return parts.join(' · ');
}
